import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import { useMediaQuery } from '@mantine/hooks'
import Slider, { Settings } from 'react-slick'
import styles from './kdt-aos-4th.module.scss'

interface IProgramProps {
  contents: {
    imgUrlM: string
    step: string
    title: string
    desc: string[]
  }[]
}

export const ProgramSliderItem = ({ contents }: IProgramProps) => {
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: false,
    slidesToShow: 3,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    className: 'carousel',
    variableWidth: true,
    responsive: [
      {
        breakpoint: 1023,
        settings: {
          slidesToShow: 1,
          variableWidth: true,
          touchMove: true,
          centerMode: false,
          centerPadding: '0',
          infinite: true,
          arrows: true,
          dots: true,
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ imgUrlM, step, title, desc }) => (
        <div key={title}>
          <div className="mx-auto">
            <div className="overflow-hidden pl-4">
              <div className="flex h-[296px] flex-col items-start justify-start overflow-hidden rounded-lg bg-trueGray-800">
                <img className="w-full" src={imgUrlM} alt="-" />
                <div className="flex flex-col gap-3 p-4">
                  <div className="flex flex-row items-center gap-1">
                    <p className="flex flex-col items-center justify-center rounded bg-[rgba(255,119,16,0.1)] px-2 py-1 text-xs font-bold text-yellow-450 lg:text-base">
                      {step}
                    </p>
                    <p className="text-base font-bold leading-normal text-white">{title}</p>
                  </div>
                  <ul className="flex w-full flex-col">
                    {desc.map(el => (
                      <p
                        className={`${styles.section4_slider_dot} text-sm leading-normal text-label-assistive lg:text-base lg:text-white`}
                        key={el}
                      >
                        {el}
                      </p>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface ICurriculumRenderProps {
  contents: {
    imgUrlPc: string
    imgUrlM: string
    title: string
    descPc?: string
    descM?: string
  }[]
}

const curriculumCustomPaging = (i: number) => {
  const dotLabels = ['Java\n+\nKotlin', 'Android\n+\n프로젝트', 'Flutter\n+\n프로젝트']
  return (
    <button>
      <span className="whitespace-pre-wrap text-center">{dotLabels[i]}</span>
    </button>
  )
}

export const CurriculumSliderItem = ({ contents }: ICurriculumRenderProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const settings = {
    dots: true,
    infinite: false,
    variableWidth: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    arrows: true,
    customPaging: curriculumCustomPaging,
  }

  return (
    <div>
      <Slider {...settings}>
        {contents.map(({ imgUrlPc, imgUrlM, title, descPc, descM }) => (
          <div className="w-full overflow-hidden pt-[138px] lg:pt-[130px]" key={title}>
            <div className="flex flex-col items-center overflow-hidden lg:flex-row lg:rounded lg:bg-[#1F1F1F]">
              <div className="w-full overflow-hidden rounded-t-lg lg:w-1/2 lg:max-w-[511px] lg:rounded-none">
                <img
                  src={isMobile ? imgUrlM : imgUrlPc}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  alt={title}
                />
              </div>
              <div className="flex w-full flex-1 flex-col items-center justify-center gap-2 rounded-b-lg bg-trueGray-800 px-4 py-5 lg:w-auto lg:items-start lg:justify-start lg:gap-3 lg:rounded-none lg:bg-[#1F1F1F] lg:px-20 lg:py-0">
                <p className="whitespace-pre-wrap text-base font-bold text-white lg:text-xl">
                  {title}
                </p>
                <p className="whitespace-pre-wrap text-center text-sm leading-normal text-label-alternative lg:text-left lg:text-base">
                  {isMobile ? descM : descPc}
                </p>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface IProjectRenderProps {
  contents: {
    imgUrlPc: string
    imgUrlM: string
    title: string
    descPc?: string
    descM?: string
    details?: {
      contentsId: string
      contentsInner?: string[]
    }[]
  }[]
}

const projectCustomPaging = (i: number) => {
  const dotLabels = ['미니\n프로젝트', '파이널\n프로젝트1', '세미\n프로젝트', '파이널\n프로젝트2']
  return (
    <button>
      <span className="whitespace-pre-wrap text-center">{dotLabels[i]}</span>
    </button>
  )
}

export const ProjectSliderItem = ({ contents }: IProjectRenderProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const settings = {
    dots: true,
    infinite: false,
    variableWidth: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    arrows: true,
    customPaging: projectCustomPaging,
  }

  return (
    <div>
      <Slider {...settings}>
        {contents.map(({ imgUrlPc, imgUrlM, title, descPc, descM, details }) => (
          <div className="w-full overflow-hidden pt-[138px] lg:pt-[130px]" key={title}>
            <div className="flex flex-col items-center overflow-hidden rounded-lg border lg:h-[288px] lg:flex-row lg:rounded lg:bg-white">
              <div className="w-full overflow-hidden rounded-t-lg lg:w-1/2 lg:max-w-[511px] lg:rounded-none">
                <img
                  src={isMobile ? imgUrlM : imgUrlPc}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  alt={title}
                />
              </div>
              <div className="flex h-full w-full flex-1 flex-col items-center justify-center gap-2 rounded-b-lg bg-white px-4 py-5 lg:w-auto lg:gap-3 lg:rounded lg:rounded-l-none lg:border-l lg:px-20 lg:py-0">
                <p className="w-full whitespace-pre-wrap text-center text-base font-bold text-label-normal lg:text-left lg:text-xl">
                  {title}
                </p>
                <p className="w-full whitespace-pre-wrap text-center text-sm leading-normal text-label-alternative lg:text-left lg:text-base">
                  {isMobile ? descM : descPc}
                </p>
                {details?.map(({ contentsId, contentsInner }) => (
                  <ul
                    key={contentsId}
                    className="flex flex-col items-center justify-center lg:w-full lg:items-start lg:justify-start"
                  >
                    {contentsInner?.map(el => (
                      <li
                        className={`${styles.section7_text_dot} max-w-fit whitespace-pre-wrap text-center text-sm leading-normal text-label-alternative lg:max-w-max lg:text-left lg:text-base`}
                        key={`${contentsId}-${el}`}
                      >
                        {el}
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface IProjectOutputProps {
  contents: {
    imgUrl: string
    title: string
    who: string
  }[]
}

export const ProjectOutputSliderItem = ({ contents }: IProjectOutputProps) => {
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 2,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: true,
    centerMode: true,
    centerPadding: '20%',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          touchMove: true,
          centerMode: true,
          centerPadding: '20px',
          dots: true,
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ imgUrl, title, who }) => (
        <div key={imgUrl} className="lg:pb-[120px]">
          <div className="mx-auto">
            <div className="overflow-hidden px-2.5 lg:pl-6 lg:pr-0">
              <div className="overflow-hidden">
                <video
                  className="w-full rounded-lg border-[3px] border-[#CFCFCF] object-cover lg:rounded-2xl"
                  src={imgUrl}
                  muted
                  autoPlay
                  playsInline
                  loop
                ></video>
                <div className="mt-[14px] flex flex-row items-center justify-center text-sm font-semibold text-label-normal lg:mt-6 lg:text-xl">
                  {title}
                </div>
                <div className="mt-1.5 flex flex-row items-center justify-center text-sm font-medium text-label-normal lg:mt-2.5 lg:text-base">
                  {who}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IReviewProps {
  contents: {
    title: string
    desc: string
    who: string
  }[]
}

export const ReviewSliderItem = ({ contents }: IReviewProps) => {
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 2,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: true,
    centerMode: true,
    centerPadding: '20%',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          speed: 500,
          touchMove: true,
          infinite: true,
          slidesToShow: 1,
          centerMode: false,
          centerPadding: '0',
          variableWidth: true,
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ title, desc, who }) => (
        <div key={title} className="pb-[65px] lg:pb-[77px]">
          <div className="mx-auto">
            <div className="overflow-hidden pl-4 lg:pl-8">
              <div className="flex flex-col gap-2 overflow-hidden rounded-lg bg-[#F2F1F0] p-4 lg:h-[367px] lg:gap-3 lg:rounded-xl lg:p-6">
                <h6 className="text-base font-semibold leading-normal text-label-normal lg:text-2xl">
                  {title}
                </h6>
                <p className="text-sm leading-normal text-label-neutral lg:text-lg">{desc}</p>
                <p className="text-sm font-semibold text-label-neutral lg:text-lg">{who}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IBenefitCarouselProps {
  contents: {
    imgUrlM: string
    imgUrlPc: string
    title: string
    desc: string
    option?: string
  }[]
}

export const BenefitSliderItem = ({ contents }: IBenefitCarouselProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: true,
    centerMode: false,
    centerPadding: '0',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          infinite: true,
          slidesToShow: 1,
          dots: true,
          arrows: false,
          centerMode: false,
          centerPadding: '0',
          variableWidth: true,
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ desc, title, imgUrlM, imgUrlPc, option }) => (
        <div key={title} className="w-full lg:pb-[77px]">
          <div className="pl-4 lg:pl-6">
            <div className="mb-[18px] overflow-hidden rounded-md bg-gray-900 lg:mb-0 lg:rounded-b-none lg:rounded-t-lg lg:bg-black">
              <img
                src={isMobile ? imgUrlM : imgUrlPc}
                className="block h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col gap-2 lg:h-[180px] lg:rounded-b-lg lg:bg-gray-900 lg:p-6">
              <p className="text-base font-bold text-white lg:text-xl">{title}</p>
              <p className="whitespace-pre-wrap text-base leading-normal text-label-assistive lg:text-white">
                {desc}
              </p>
              {option && <p className="text-xs text-label-alternative lg:text-sm">{option}</p>}
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}
